// InstantMessageList - MODULE
// ***********************************************************************************************************************
import { Box } from "@mui/material";
import * as React from "react";
import { ToolButtonDropdown } from "ui/Scripts/source/components/framework/Button/ToolButtonDropdown";
import { Base } from "ui/Scripts/source/framework/base";
import { FileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { ConfirmationDialogResult, FileType, InstantMessageReadState } from "ui/Scripts/source/models/enums";
import { IInstantMessageChannelItem } from "ui/Scripts/source/models/instantMessage/instantMessageChannelItem";
import { IInstantMessageListItem, InstantMessageListItem } from "ui/Scripts/source/models/instantMessage/instantMessageListItem";
import { SignalRConnectionManager } from "ui/Scripts/source/models/signalR/signalRConnectionManager";
import { Translations } from "ui/Scripts/source/models/translations";
import { SaveAttachmentBase64 } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/common/saveAttachmentBase64";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import * as instantMessageService from "../../../models/services/instantMessageService";
import { ISessionState } from "../../../models/session/sessionState";
import { StoreUtils } from "../../../models/store/storeUtils";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { PageListActionContainer } from "../../framework/List/PageListActionContainer";
import { MessageListContainer } from "../../framework/Message/MessageListContainer";
import { NewMessage } from "../../framework/Message/NewMessage";
import { InstantMessageListLine } from "../InstantMessageListLine";
import { List } from "source/components/framework/List/List";
import { AppMoreIcon } from "source/components/framework/Icon/AppMoreIcon";

// ChannelInstantMessageNew
export interface IChannelInstantMessageNewProp {
    sessionState: ISessionState;
    maxInstantMessageAttachmentSize: number;
    companyId: string;
    channelId: string;
    channelIsReadOnly: boolean;
    userCode: string;
    replyItem: IInstantMessageListItem;
    onInstantMessageSaved: (item: IInstantMessageListItem) => void;
    onDropAttachments: (e: any) => Promise<void>;
    onDragOverAttachments: (e: any) => void;
    onDragEnterAttachments: (e: any) => void;
    onDragLeaveAttachments: (e: any) => void;
    onInputRowCountChanged: (rowCount: number) => void;
    onReplyContainerSizeChanged: (height: number) => void;
    onRemoveReply: () => void;
    getFileAttachment: (blob: any, category: number) => Promise<FileAttachment>;
}

interface IChannelInstantMessageNewState {
    content: string;
    attachments: FileAttachment[];
    sending: boolean;
}

export class ChannelInstantMessageNew extends React.Component<IChannelInstantMessageNewProp, IChannelInstantMessageNewState> {
    //private inputElement: HTMLTextAreaElement;
    //private inputHiddenElement: HTMLTextAreaElement;
    //private rowCount = 1;

    constructor(props: IChannelInstantMessageNewProp) {
        super(props);
        this.state = {
            content: "",
            attachments: [],
            sending: false
        };
    }

    componentDidUpdate(prevProps: IChannelInstantMessageNewProp, prevState: IChannelInstantMessageNewState) {
        const props = this.props;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const prevChannelId = prevProps.channelId;
        const channelId = props.channelId;
        if (companyId === prevCompanyId && prevChannelId === channelId) return;
        this.setState({
            content: "",
            attachments: [],
        });
        this.props.onRemoveReply();
    }

    getSendingDisabled = (): boolean => {
        const state = this.state;
        return state.sending || !state.content && state.attachments.length < 1;
    };

    getSaveDataFromState = async (props: IChannelInstantMessageNewProp, state: IChannelInstantMessageNewState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("channelId", props.channelId);
        result.append("replyItemId", props.replyItem ? props.replyItem.id : null);
        result.append("content", state.content);
        result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(state.attachments.filter(i => !!i.file)));
        return result;
    };

    updateSavedMessageToList = (id: string) => {
        const props = this.props;
        const state = this.state;
        const newMessage = new InstantMessageListItem();
        newMessage.id = id;
        newMessage.channelId = props.channelId;
        newMessage.content = state.content;
        newMessage.modifyUser = StoreUtils.getNowModifyUser();
        newMessage.attachments = state.attachments.slice(0);
        if (props.replyItem) {
            newMessage.replyToInstantMessage = new InstantMessageListItem(props.replyItem);
        }
        props.onInstantMessageSaved(newMessage);
    };

    // updateInputRowCount = (rowCount: number) => {
    //     const defaultClientHeight = 38;
    //     const defaultHeight = defaultClientHeight + 2;
    //     if (this.rowCount !== rowCount) {
    //         if (rowCount > 1) {
    //             this.inputElement.style.height = (defaultHeight + ((rowCount - 1) * 24)).toString(10) + "px";
    //         } else {
    //             this.inputElement.style.removeProperty("height");
    //         }
    //         this.props.onInputRowCountChanged(rowCount);
    //         this.rowCount = rowCount;
    //     }
    // };

    saveInstantMessage = async () => {
        const props = this.props;
        const state = this.state;
        if (this.getSendingDisabled()) return;
        this.setState({ sending: true });
        try {
            const saveData = await this.getSaveDataFromState(props, state);
            //Call server
            const result = await AppUtils.callService(() => instantMessageService.saveInstantMessage(saveData.obj), false, true);
            if (!result) return null;
            this.updateSavedMessageToList(result.id);
            this.setState({
                content: "",
                attachments: [],
            });
            //this.updateInputRowCount(1);
        } finally {
            this.setState({ sending: false });
        }
    };

    handleChange = (event) => {
        this.setState({ content: event.target.value });
    };

    // #region Attachments
    handleAddAttachment = async (fileList: FileList) => {
        const state = this.state;
        const attachments = await FileAttachment.addFileListToFileAttachments(state.attachments, fileList, 0);
        if (attachments.length < 1) return;
        for (const attachment of attachments) {
            if (!AppUtils.validateFileAttachment(attachment, Translations.MaximumSizeOfInstantMessageAttachmentIsParameterMB, this.props.maxInstantMessageAttachmentSize)) return;
        }
        if (!AppUtils.validateFileAttachments(attachments)) return;
        this.setState({
            attachments
        });
    };

    handleAddAttachments = (newAttachments: FileAttachment[]) => {
        if (!newAttachments || newAttachments.length < 1) return;
        const attachments = this.state.attachments.slice(0);
        for (const newAttachment of newAttachments) {
            if (!newAttachment) return;
            attachments.push(newAttachment);
        }
        if (attachments.length < 1 || !AppUtils.validateFileAttachments(attachments)) return;
        this.setState({
            attachments
        });
    };

    handleSetAttachmentDropZone = (value: boolean) => {
        // if (!this.inputElement) return;
        // if (value) {
        //     this.inputElement.classList.add("draggingOver");
        // } else {
        //     this.inputElement.classList.remove("draggingOver");
        // }
    };

    handleRemoveAttachment = async (id: string) => {
        const state = this.state;
        if (!id) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveAttachmentParameter, state.attachments.find(x => x.id === id)?.name), false) === ConfirmationDialogResult.No) return;
        const attachments = state.attachments.filter(i => i.id !== id);
        this.setState({
            attachments
        });
    };
    // #endregion Attachments
    
    // handleTextAreaInput = (e: any) => {
    //     this.inputHiddenElement.value = this.inputElement.value;
    //     this.inputHiddenElement.style.width = (this.inputElement.clientWidth + 2) + "px";
    //     window.setTimeout(() => {
    //         const rowCount = Math.min(Math.ceil(this.inputHiddenElement.scrollHeight / this.inputHiddenElement.clientHeight), 3);
    //         this.updateInputRowCount(rowCount);
    //     }, 0);
    // };

    render() {
        const state = this.state;
        const props = this.props;
        const imageAttachments = state.attachments.filter(i => i.type === FileType.Image);
        const fileAttachments = state.attachments.filter(i => i.type !== FileType.Image);
        return (
            <Box sx={{ margin: ".5rem" }}>
                <NewMessage
                    message={state.content}
                    fileAttachments={fileAttachments}
                    imageAttachments={imageAttachments}
                    placeholder={!props.channelIsReadOnly ? Translations.WriteMessage : Translations.ThisChannelHasBeenArchived}
                    disabled={props.channelIsReadOnly}
                    sendDisabled={props.channelIsReadOnly || this.getSendingDisabled()}
                    sendButtonTooltip={Translations.Send}
                    replyText={props.replyItem?.getText(props.userCode)}
                    replySender={props.replyItem?.modifyUser?.name}
                    replyDate={props.replyItem?.modifyUser?.date}
                    onChange={this.handleChange}
                    onSend={this.saveInstantMessage}
                    onAddAttachment={this.handleAddAttachment}
                    onRemoveAttachment={this.handleRemoveAttachment}
                    onRemoveReply={props.onRemoveReply}
                />
            </Box>
        );
    }
}

// ChannelInstantMessages
const instantMessageListPageSize = 20;

export interface IChannelInstantMessagesProp {
    sessionState: ISessionState;
    maxInstantMessageAttachmentSize: number;
    companyId: string;
    channelId: string;
    channelIsReadOnly: boolean;
    userCode: string;
    userIsChannelAdmin: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    filterActive: boolean;
    onInstantMessageUpdated: (item: IInstantMessageListItem) => void;
    onInstantMessageChannelRemoved: (id: string) => void;
    onInstantMessageChannelUpdated: (item: IInstantMessageChannelItem) => void;
    onInstantMessageChannelSetHasUnreadMessagesOnList: (value: boolean) => void;
}

interface IChannelInstantMessagesState {
    hasMore: boolean;
    isLoading: boolean;
    pageSize: number;
    page: number;
    selectedId: string;
    checkedIds: string[];
    items: IInstantMessageListItem[];
    replyItem: IInstantMessageListItem;
}

export class ChannelInstantMessages extends React.Component<IChannelInstantMessagesProp, IChannelInstantMessagesState> {
    private containerDiv = React.createRef<HTMLDivElement>();
    private signalRConnectionManager: SignalRConnectionManager;
    private readReceiptProcessingIds: string[] = [];
    private readReceiptProcessedIds: string[] = [];
    private observer: IntersectionObserver;
    private channelInstantMessageNewRef = React.createRef<ChannelInstantMessageNew>();
    private newMessageRowCount = 1;
    private replyContainerHeight = 0;

    intersectionCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.75) continue;
            const line = entry.target as HTMLDivElement;
            if (!line) continue;
            const id = line.getAttribute("data-id");
            if (!id) return;
            const item = this.state.items.find(i => i.id === id);
            if (!item || item.isUserRead()) continue;
            const processing = this.readReceiptProcessingIds.indexOf(id) > -1;
            if (processing) continue;
            const processed = this.readReceiptProcessedIds.indexOf(id) > -1;
            if (processed) continue;
            this.readReceiptProcessingIds.push(id);
            instantMessageService.sendInstantMessageReadReceipts([id])
                .then(result => {
                    if (result) {
                        this.readReceiptProcessedIds.push(id);
                    }
                })
                .finally(() => {
                    this.readReceiptProcessingIds = this.readReceiptProcessingIds.filter(i => i !== id);
                });
        }
    };

    createObserver = () => {
        this.observer = new IntersectionObserver(this.intersectionCallback, {
            root: this.containerDiv.current,
            rootMargin: "0px",
            threshold: 0.9
        });
    };

    removeObserver = () => {
        if (!this.observer) return;
        this.observer.disconnect();
    };

    constructor(props: IChannelInstantMessagesProp) {
        super(props);
        this.state = {
            hasMore: true,
            isLoading: false,
            pageSize: instantMessageListPageSize,
            page: 1,
            selectedId: null,
            checkedIds: [],
            items: [],
            replyItem: null
        };
    }

    getIsFilteredList = (): boolean => {
        return !!this.props.filter;
    };

    scrollToBottom = () => {
        this.containerDiv.current?.scrollTo({
            top: this.containerDiv.current?.scrollHeight,
            left: 0,
            behavior: "auto"
        });
    };

    scrollToId = (id: string) => {
        if (!id) return;
        const elements = this.containerDiv.current?.getElementsByClassName("id" + id);
        if (elements.length > 0 && !Base.isNullOrUndefined(elements[0])) {
            const y = Math.max((elements[0] as HTMLDivElement).offsetTop - 100, 0);
            this.containerDiv.current?.scrollTo({
                top: y,
                left: 0,
                behavior: "auto"
            });
        }
    };

    addTitlesToList = (items: IInstantMessageListItem[]): IInstantMessageListItem[] => {
        const result: IInstantMessageListItem[] = [];
        const listItems = items.filter(i => !i.isTitle);
        if (listItems.length < 1) return result;
        const yesterdayDayStr = Base.timeToDateStr(new Date().addDays(-1).getTime());
        const todayDayStr = Base.timeToDateStr(new Date().getTime());
        let currentDayStr = todayDayStr;
        for (let i = listItems.length - 1; i >= 0; i --) {
            const item = listItems[i];
            const itemDayStr = Base.timeToDateStr(item.modifyUser.date);
            if (currentDayStr !== itemDayStr) {
                result.unshift(InstantMessageListItem.createTitleItem(itemDayStr === todayDayStr
                    ? Translations.Today
                    : (itemDayStr === yesterdayDayStr
                        ? Translations.Yesterday
                        : Base.timeToWeekdayStr(item.modifyUser.date) + " " + itemDayStr)));
                currentDayStr = itemDayStr;
            }
            result.unshift(item);
        }
        return result;
    };

    searchItems = async (pageSize: number, page: number, resetItems: boolean, refreshList: boolean, scrollToBottom = true) => {
        const props = this.props;
        if (!props.companyId || !props.channelId) return;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => instantMessageService.getInstantMessageListItems(props.companyId, props.channelId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1,
            props.filter, props.sortColumn, props.sortOrderIsAsc), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(this.state.items, this.state.selectedId, this.state.checkedIds, result.items, resetItems, refreshList);
        this.updateHasUnreadMessagesOnList(items.items);
        this.setState({
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            items: this.addTitlesToList(items.items),
            selectedId: items.selectedId,
            checkedIds: items.checkedIds,
        }, () => {
            if (scrollToBottom) {
                this.scrollToBottom();
            } else {
                this.scrollToId(result.items.length > 0 ? result.items[0].id : "");
            }
        });
    };

    handleSearchMessages = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, !nextPage, false, false);
    };

    handleScrollSub = Base.debounce((obj: ChannelInstantMessages) => {
        const state = obj.state;
        const props = obj.props;
        if (state.isLoading || !state.hasMore) return;
        if (obj.containerDiv.current?.scrollTop < 5) {
            obj.handleSearchMessages(props.sortColumn, props.sortOrderIsAsc, true);
        }
    }, 100);
    
    handleScroll = (event) => {
        this.handleScrollSub(this);
    };

    updateHasUnreadMessagesOnList = (items: IInstantMessageListItem[]) => {
        const props = this.props;
        props.onInstantMessageChannelSetHasUnreadMessagesOnList(items.findIndex(i => !i.isTitle && !i.isDeleted() && i.modifyUser.code !== props.userCode && !i.modifyUser.isRemoved() && !i.isUserRead()) > -1);
    };

    updateInstantMessageItemToList = (item: IInstantMessageListItem) => {
        const props = this.props;
        props.onInstantMessageUpdated(item);
        if (item.channelId !== props.channelId || !!props.filter) return;
        const oldItem = this.state.items.find(i => i.id === item.id);
        const isNew = !oldItem || oldItem.isNew();
        const items = this.state.items.filter(i => i.id !== item.id);
        const readReceiptSendItems = items.filter(i => this.readReceiptProcessedIds.indexOf(i.id) > -1);
        for (const readReceiptSendItem of readReceiptSendItems) {
            readReceiptSendItem.readState = InstantMessageReadState.UserRead;
        }
        items.push(item);
        InstantMessageListItem.sortInstantMessageListItems(items, props.sortColumn, props.sortOrderIsAsc);
        this.updateHasUnreadMessagesOnList(items);
        this.setState({
            items: this.addTitlesToList(items),
            replyItem: null
        }, () => {
            if (!isNew) return;
            window.setTimeout(() => {
                this.scrollToBottom();
            }, 50);
        });
    };

    handleInstantMessageChannelUpdated = (item: IInstantMessageChannelItem) => {
        const props = this.props;
        props.onInstantMessageChannelUpdated(item);
        this.updateHasUnreadMessagesOnList(this.state.items);
    };

    componentDidMount(): void {
        const state = this.state;
        this.containerDiv.current?.addEventListener("scroll", this.handleScroll);
        //Observer
        this.createObserver();
        //Search
        this.searchItems(state.pageSize, state.page, false, false);
        //SignalR
        this.signalRConnectionManager = new SignalRConnectionManager();
        this.signalRConnectionManager.openConnection(appConfig.rootUrl, {
            onUpdateInstantMessageListLine: this.updateInstantMessageItemToList,
            onRemoveInstantMessageChannel: this.props.onInstantMessageChannelRemoved,
            onUpdateInstantMessageChannel: this.handleInstantMessageChannelUpdated
        });
    }

    componentWillUnmount(): void {
        this.containerDiv.current?.removeEventListener("scroll", this.handleScroll);
        //SignalR
        this.signalRConnectionManager.closeConnection();
        //Observer
        this.removeObserver();
    }

    componentDidUpdate(prevProps: IChannelInstantMessagesProp, prevState: IChannelInstantMessagesState) {
        const props = this.props;
        const state = this.state;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const prevChannelId = prevProps.channelId;
        const channelId = props.channelId;
        const prevFilter = prevProps.filter ? prevProps.filter : "";
        const filter = props.filter ? props.filter : "";
        const prevSortColumn = prevProps.sortColumn ? prevProps.sortColumn : "";
        const sortColumn = props.sortColumn ? props.sortColumn : "";
        const prevSortOrderIsAsc = prevProps.sortOrderIsAsc ? prevProps.sortOrderIsAsc : false;
        const sortOrderIsAsc = props.sortOrderIsAsc ? props.sortOrderIsAsc : false;
        if (companyId === prevCompanyId && prevChannelId === channelId && prevFilter === filter && prevSortColumn === sortColumn && prevSortOrderIsAsc === sortOrderIsAsc) return;
        this.searchItems(state.pageSize, 1, true, false);
    }

    handleSetSelected = (id: string) => {
        if (!id) return;
        this.setState({
            selectedId: id
        });
    };

    handleReplyToInstantMessage = (item: IInstantMessageListItem) => {
        this.setState({
            replyItem: item
        });
    };

    // #region drag and drop
    getFileAttachment = async (blob: any, category: number): Promise<FileAttachment> => {
        const attachment = await (blob instanceof File
            ? FileAttachment.fileToFileAttachment(blob, category)
            : FileAttachment.blobToFileAttachment(blob, category));
        if (!attachment) return null;
        if (!AppUtils.validateFileAttachment(attachment, Translations.MaximumSizeOfInstantMessageAttachmentIsParameterMB, this.props.maxInstantMessageAttachmentSize)) return null;
        return attachment;
    };

    handleDropAttachments = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        this.channelInstantMessageNewRef.current.handleSetAttachmentDropZone(false);
        const attachments: FileAttachment[] = [];
        const files: any[] = [];
        if (e.dataTransfer.items) {
            [...e.dataTransfer.items].forEach((item, i) => {
                if (item.kind === "file") {
                    files.push(item.getAsFile());
                }
            });
        } else {
            [...e.dataTransfer.files].forEach((file, i) => {
                files.push(file);
            });
        }
        if (files.length < 1) return;
        for (const file of files) {
            const attachment = await this.getFileAttachment(file, 0);
            if (!attachment) return;
            attachments.push(attachment);
        }
        if (attachments.length < 1 || !AppUtils.validateFileAttachments(attachments)) return;
        if (!this.channelInstantMessageNewRef || !this.channelInstantMessageNewRef.current) return;
        this.channelInstantMessageNewRef.current.handleAddAttachments(attachments);
    };
    
    handleDragEnterAttachments = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        window.setTimeout(() => {
            this.channelInstantMessageNewRef.current.handleSetAttachmentDropZone(true);
        }, 100);
    };
    
    handleDragLeaveAttachments = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        this.channelInstantMessageNewRef.current.handleSetAttachmentDropZone(false);
    };

    handleDragOverAttachments = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
    };
    // #endregion drag and drop

    handleInputRowCountChanged = (rowCount: number) => {
        const amount = 305 + (24 * (rowCount - 1)) + this.replyContainerHeight;
        if (rowCount <= 1 && this.replyContainerHeight < 1) {
            this.newMessageRowCount = 1;
            this.containerDiv.current?.style.removeProperty("height");
        } else {
            this.newMessageRowCount = rowCount;
            this.containerDiv.current?.style.setProperty("height", "calc(100vh - " + amount.toString(10) + "px)");
        }
    };
    
    handleReplyContainerSizeChanged = (height: number) => {
        //console.log("replyContainerHeight, newMessageRowCount", height, this.newMessageRowCount)
        this.replyContainerHeight = height;
        this.handleInputRowCountChanged(this.newMessageRowCount);
    };

    handleRemoveReply = () => {
        this.setState({
            replyItem: null
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 2, overflow: "hidden" }}>
                <div
                    style={{ display: "flex", flexDirection: "column", flexGrow: 2, margin: "1rem", overflow: "hidden" }}
                    onDrop={this.handleDropAttachments}
                    onDragOver={this.handleDragOverAttachments}
                    onDragEnter={this.handleDragEnterAttachments}
                    onDragLeave={this.handleDragLeaveAttachments}
                >
                    <MessageListContainer ref={this.containerDiv} height="calc(100vh - 340px)" padding="0 15%">
                        {state.items.map((item) =>
                            <InstantMessageListLine
                                key={item.id}
                                observer={this.observer}
                                channelId={props.channelId}
                                channelIsReadOnly={props.channelIsReadOnly}
                                userCode={props.userCode}
                                userIsChannelAdmin={props.userIsChannelAdmin}
                                selectedId={state.selectedId}
                                item={item}
                                onSetSelected={this.handleSetSelected}
                                onReplyToInstantMessage={this.handleReplyToInstantMessage}
                            />
                        ).reverse()}
                    </MessageListContainer>
                </div>
                {!props.channelIsReadOnly && !props.filterActive &&
                    <ChannelInstantMessageNew
                        ref={this.channelInstantMessageNewRef}
                        maxInstantMessageAttachmentSize={props.maxInstantMessageAttachmentSize}
                        sessionState={props.sessionState}
                        companyId={props.companyId}
                        channelId={props.channelId}
                        channelIsReadOnly={props.channelIsReadOnly}
                        userCode={props.userCode}
                        replyItem={state.replyItem}
                        onInstantMessageSaved={this.updateInstantMessageItemToList}
                        onDropAttachments={this.handleDropAttachments}
                        onDragOverAttachments={this.handleDragOverAttachments}
                        onDragEnterAttachments={this.handleDragEnterAttachments}
                        onDragLeaveAttachments={this.handleDragLeaveAttachments}
                        onInputRowCountChanged={this.handleInputRowCountChanged}
                        onReplyContainerSizeChanged={this.handleReplyContainerSizeChanged}
                        onRemoveReply={this.handleRemoveReply}
                        getFileAttachment={this.getFileAttachment}
                    />
                }
            </Box>
        );
    }
}

// ChannelInstantMessageList
export interface IChannelInstantMessageListProp {
    sessionState: ISessionState;
    companyId?: string;
    userCode: string;
    channel: IInstantMessageChannelItem;
    dataId?: string;
    maxInstantMessageAttachmentSize: number;
    onFilterActiveChanged: (filterActive: boolean) => void;
    onEditChannel: (channelId: string) => void;
    onExitChannel: (channelId: string) => void;
    onInstantMessageUpdated: (item: IInstantMessageListItem) => void;
    onInstantMessageChannelRemoved: (id: string) => void;
    onInstantMessageChannelUpdated: (item: IInstantMessageChannelItem) => void;
    onInstantMessageChannelSetHasUnreadMessagesOnList: (value: boolean) => void;
}

interface IChannelInstantMessageListState {
    channelId: string;
    channelIsReadOnly: boolean;
    userIsChannelAdmin: boolean;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    filter: string;
    filterActive: boolean;
}

export class ChannelInstantMessageList extends React.Component<IChannelInstantMessageListProp, IChannelInstantMessageListState> {
    getChannelId = (props: IChannelInstantMessageListProp): string => {
        return props.channel ? props.channel.id : "";
    };

    getChannelIsReadOnly = (props: IChannelInstantMessageListProp): boolean => {
        return props.channel ? !props.channel.active : true;
    };
    
    getUserIsChannelAdmin = (props: IChannelInstantMessageListProp): boolean => {
        return props.channel ? props.channel.userIsAdmin : false;
    };

    constructor(props: IChannelInstantMessageListProp) {
        super(props);
        this.state = {
            channelId: this.getChannelId(props),
            channelIsReadOnly: this.getChannelIsReadOnly(props),
            userIsChannelAdmin: this.getUserIsChannelAdmin(props),
            sortColumn: "modifyDate",
            sortOrderIsAsc: false,
            filter: "",
            filterActive: false
        };
    }

    componentDidUpdate(prevProps: IChannelInstantMessageListProp, prevState: IChannelInstantMessageListState) {
        const props = this.props;
        const prevDataId = prevProps.dataId ? prevProps.dataId : "";
        const dataId = props.dataId ? props.dataId : "";
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const prevChannelId = this.getChannelId(prevProps);
        const channelId = this.getChannelId(props);
        const prevChannelIsReadOnly = this.getChannelIsReadOnly(prevProps);
        const channelIsReadOnly = this.getChannelIsReadOnly(props);
        if (dataId === prevDataId && companyId === prevCompanyId && prevChannelId === channelId && prevChannelIsReadOnly === channelIsReadOnly) return;
        this.setState({
            channelId,
            channelIsReadOnly,
            userIsChannelAdmin: this.getUserIsChannelAdmin(props),
            sortColumn: "modifyDate",
            sortOrderIsAsc: false,
            filter: "",
            filterActive: false
        });
        props.onFilterActiveChanged(false);
    }

    changeFilter = (filter: string) => {
        this.setState({
            filter,
            filterActive: !!filter
        });
        this.props.onFilterActiveChanged(!!filter);
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!props.channel) {
            return null;
        }
        return (
            <>
                <PageListActionContainer
                    leftItems={
                        <ListSearchFilter
                            active={state.filterActive}
                            searchFilter={state.filter}
                            onSearchClick={this.changeFilter}
                        />
                    }
                    rightItems={
                        <ToolButtonDropdown
                            variant="icon"
                            actions={[
                                { title: Translations.InstantMessageChannelInformation, onClick: () => props.onEditChannel(state.channelId) },
                                { title: Translations.ExitInstantMessageChannel, onClick: () => props.onExitChannel(state.channelId), disabled: props.channel.userIsOnlyAdmin }
                            ]}
                            icon={<AppMoreIcon />}
                            tooltip={Translations.AdditionalFunctions}
                            menuRight={true}
                        />
                    }
                />
                <ChannelInstantMessages
                    sessionState={props.sessionState}
                    maxInstantMessageAttachmentSize={props.maxInstantMessageAttachmentSize}
                    companyId={props.companyId}
                    channelId={state.channelId}
                    channelIsReadOnly={state.channelIsReadOnly}
                    userCode={props.userCode}
                    userIsChannelAdmin={state.userIsChannelAdmin}
                    filter={state.filter}
                    filterActive={state.filterActive}
                    sortColumn={state.sortColumn}
                    sortOrderIsAsc={state.sortOrderIsAsc}
                    onInstantMessageUpdated={props.onInstantMessageUpdated}
                    onInstantMessageChannelRemoved={props.onInstantMessageChannelRemoved}
                    onInstantMessageChannelUpdated={props.onInstantMessageChannelUpdated}
                    onInstantMessageChannelSetHasUnreadMessagesOnList={props.onInstantMessageChannelSetHasUnreadMessagesOnList}
                />
            </>
        );
    }
}